import { ExportProps } from '~types/exportProps';

import { formatDateString } from '~libs/dayjs';
import { formatCurrency } from '~libs/dataUtils';

import { selectLookupClients } from '~main/selectors/clients/clients';

const formatCurrencyValue = (value: any) =>
  value === null || value === undefined || value === '' ? '' : formatCurrency(value as string);

const fields: ExportProps = {
  client_id: {
    type: 'multi-lookup',
    field: 'claim_item.client_id',
    caption: 'Client',
    placeholder: 'All',
    selectorOptions: selectLookupClients,
    columnDef: {
      id: 'client_id',
      header: 'Client',
      type: 'text',
      deriveValue: (row) => row.full_name ?? '',
    },
  },
  reference: {
    type: 'text-like',
    caption: 'Reference',
    checked: true,
    field: 'reconciliation.reference',
    placeholder: 'Filter...',
    columnDef: {
      id: 'reference',
      header: 'Reference',
      type: 'text',
    },
  },
  service_name: {
    type: 'text-like',
    caption: 'SAH Service Type',
    checked: true,
    // The reconciliation items query only joins claim_item.sah_service (see
    // company_sah_reconciliation_items.mjs defaultInclude), not the deeper
    // claim_item.sah_item.service path this used to point at. That mismatched path passed
    // the API's association-graph validation but had no matching JOIN, surfacing as a
    // Postgres "missing FROM-clause entry" error whenever this filter was actually used
    // (TAV-2850). columnField keeps the exported CSV column keyed the same as before.
    // Safe to reuse the un-joined path here: company_sah_reconciliation_items.mjs's
    // export_csv destructures `columns` off req.query before it ever reaches the query
    // builder, so col.id (this value) is only ever used as a lookup key into the
    // in-memory COLUMN_EXTRACTORS map against already-fetched rows, never as a WHERE/JOIN
    // path, so it cannot reproduce the FROM-clause error.
    field: 'claim_item.sah_service.name',
    columnField: 'claim_item.sah_item.service.name',
    placeholder: 'Filter...',
    columnDef: {
      id: 'service_name',
      header: 'SAH Service Type',
      type: 'text',
    },
  },
  service_date: {
    type: 'date-range',
    field: 'claim_item.service_date',
    caption: 'Service Date',
    columnDef: {
      id: 'claim_item.service_date',
      header: 'Service Date',
      type: 'text',
      deriveValue: (row) => formatDateString(row.service_date),
    },
  },
  claimed: {
    type: 'text',
    field: 'claimed',
    caption: 'Claimed Amount',
    checked: true,
    columnDef: {
      id: 'claimed',
      header: 'Claimed Amount',
      type: 'text',
      deriveValue: (row) => formatCurrencyValue(row.claimed),
    },
  },
  paid: {
    type: 'text',
    field: 'paid',
    caption: 'Paid Amount',
    checked: true,
    columnDef: {
      id: 'paid',
      header: 'Paid Amount',
      type: 'text',
      deriveValue: (row) => formatCurrencyValue(row.paid),
    },
  },
  gov_contribution_amount: {
    type: 'text',
    field: 'gov_contribution_amount',
    caption: 'Government Contributions',
    checked: true,
    columnDef: {
      id: 'gov_contribution_amount',
      header: 'Government Contribution',
      type: 'text',
      deriveValue: (row) => formatCurrencyValue(row.gov_contribution_amount),
    },
  },
  co_contribution_amount: {
    type: 'text',
    field: 'co_contribution_amount',
    caption: 'Client Contribution',
    checked: true,
    columnDef: {
      id: 'co_contribution_amount',
      header: 'Client Contribution',
      type: 'text',
      deriveValue: (row) => formatCurrencyValue(row.co_contribution_amount),
    },
  },
};

export default fields;
